import { IDatabaseTableModel, IDataDiagramSchemeDto, IDatasourceDto } from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import { IScreenDesignContextState } from "../../../contexts/screen-design-context/screen-design-context";
import { DataDiagramSchemeService } from "../../../services/data-diagram-scheme-service";
import { DatasourceService } from "../../../services/datasource-service";
import { KMainFunctions } from "../../../shared/utility/main-functions";

/**
 * Renaming a datasource or a table from a row — the Sources tree's inline rename
 * (`changeName` / `upddate` in `data-diagram-scheme-window.tsx`) as a rule set and two writes, so
 * the Datasources landing's rename dialog judges a name the way the tree does and stores it
 * through the same calls.
 */

/** A letter, then letters, digits or underscores: the name becomes a generated identifier. */
export const RENAME_NAME_PATTERN: RegExp = /^[A-Za-z][A-Za-z0-9_]+$/;

/** The names a renamed datasource may not take. */
export interface IDatasourceRenameNames {
  screenNames: string[];
  processNames: string[];
  /** The datasource's own actions. */
  actionNames: string[];
}

/** The names a renamed table may not take. */
export interface ITableRenameNames {
  screenNames: string[];
  /** The managed schema's other tables. */
  otherTableNames: string[];
}

/** Case-insensitive, and deliberately not locale-aware — the tree compared with `toLowerCase`. */
const isTaken = (names: string[], name: string): boolean => {
  const lowered: string = name.toLowerCase();
  return names.some((candidate: string): boolean => (candidate ?? "").toLowerCase() === lowered);
};

/** The format checks both kinds share, with the kind's own word in the message. */
const getFormatProblem = (kindLabel: string, name: string): string | undefined => {
  if (!name) return `Please enter a name for the ${kindLabel}.`;
  if (!RENAME_NAME_PATTERN.test(name)) return `${kindLabel} name is invalid.`;
  return undefined;
};

/** Why the datasource cannot take this name, in the tree's words; undefined when it can. */
export const getDatasourceRenameProblem = (name: string, names: IDatasourceRenameNames): string | undefined => {
  const formatProblem: string | undefined = getFormatProblem("Datasource", name);
  if (formatProblem) return formatProblem;
  if (isTaken(names.screenNames, name) || isTaken(names.processNames, name)) {
    return "Datasource name can not be equal to screen name or process name.";
  }
  if (isTaken(names.actionNames, name)) return "Datasource name cannot be the same as its child actions' name.";
  return undefined;
};

/** Why the table cannot take this name, in the tree's words; undefined when it can. */
export const getTableRenameProblem = (name: string, names: ITableRenameNames): string | undefined => {
  const formatProblem: string | undefined = getFormatProblem("Table", name);
  if (formatProblem) return formatProblem;
  // The tree holds a table to screen names only; process names are a datasource's concern.
  if (isTaken(names.screenNames, name)) return "Table name can not be equal to screen name or process name.";
  if (isTaken(names.otherTableNames, name)) return "Table name already exist.";
  return undefined;
};

/**
 * The managed schema's own copy of a table — the one a rename migrates, columns included. Looked up
 * by name, as the tree does; undefined once the table has left the schema.
 */
const findDiagramTable = (sd: IScreenDesignContextState, table: IDatabaseTableModel): IDatabaseTableModel | undefined =>
  sd.databaseDiagramData?.leftMenuData?.tables?.find(
    (candidate: IDatabaseTableModel): boolean => candidate.name === table.name
  );

/** The names `getTableRenameProblem` holds a table's new name against. */
export const getTableRenameNames = (sd: IScreenDesignContextState, table: IDatabaseTableModel): ITableRenameNames => {
  const own: IDatabaseTableModel | undefined = findDiagramTable(sd, table);
  return {
    screenNames: (sd.screenList ?? []).map((screen): string => screen.name),
    otherTableNames: (sd.databaseDiagramData?.leftMenuData?.tables ?? [])
      .filter((candidate: IDatabaseTableModel): boolean => candidate.id !== own?.id)
      .map((candidate: IDatabaseTableModel): string => candidate.name)
  };
};

/** The names `getDatasourceRenameProblem` holds a datasource's new name against. */
export const getDatasourceRenameNames = (
  sd: IScreenDesignContextState,
  datasource: IDatasourceDto
): IDatasourceRenameNames => ({
  screenNames: (sd.screenList ?? []).map((screen): string => screen.name),
  processNames: (sd.workFlowList ?? []).map((process): string => process.name),
  actionNames: (sd.actionDefinitions ?? [])
    .filter((action): boolean => action.kDatasource_Id === datasource.id)
    .map((action): string => action.name)
});

/**
 * Saves the datasource under its new name. True once stored; a failure has been reported to the
 * user by the time it resolves false. The save carries a copy — the provider's datasource is not
 * renamed in place ahead of the server.
 */
export const renameDatasourceFromRow = async (
  sd: IScreenDesignContextState,
  datasource: IDatasourceDto,
  name: string
): Promise<boolean> => {
  try {
    const response: AxiosResponse | undefined = await DatasourceService.SaveDatasource({ ...datasource, name });
    if (!response || response.status !== 200) return false;
    sd.refreshDatabases();
    sd.refreshActions();
    KMainFunctions.displaySuccessNotification("Datasource name updated successfully.");
    return true;
  } catch (error) {
    KMainFunctions.exceptionHandler(error);
    return false;
  }
};

/**
 * Renames a managed table: a migration of the schema's own copy of it under the new name, with its
 * columns, as the tree runs it. True once stored.
 */
export const renameTableFromRow = async (
  sd: IScreenDesignContextState,
  table: IDatabaseTableModel,
  name: string
): Promise<boolean> => {
  const own: IDatabaseTableModel | undefined = findDiagramTable(sd, table);
  if (!own) {
    KMainFunctions.displayWarningNotification("This table is no longer in the database. Refresh and try again.");
    return false;
  }
  const renamed: IDatabaseTableModel = {
    id: own.id,
    name,
    schema: own.schema,
    dropped: own.dropped,
    columns: own.columns
  } as IDatabaseTableModel;
  const scheme: IDataDiagramSchemeDto = { tables: [renamed] } as IDataDiagramSchemeDto;

  try {
    const response: AxiosResponse = await DataDiagramSchemeService.ExecuteMigrationScript(scheme);
    if (response.status !== 200) return false;
    sd.refreshTables();
    sd.updateDatabaseDiagramData();
    KMainFunctions.displaySuccessNotification("Table name updated successfully.");
    return true;
  } catch (error) {
    KMainFunctions.exceptionHandler(error);
    return false;
  }
};
